import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TRANSIT_MS as MS, preloadArt, REFUSE_LINE } from '../../utils/roomTransit';

// 🚪🕯️ BETWEEN ROOMS (Nina 09-24): the drawing + the timeline for a room move.
// Rules and copy = utils/roomTransit.js. Mockup lineage: v1 (the four styles,
// https://claude.ai/artifact/XoxXt2Db2r6H7AYgQedFWk); Nina's split: A footsteps
// for places you walk to, B the little door for rooms behind a door, C the
// keyhole for outings and outdoor events (D is CandleHop, not a move).
//
// ⭐⭐ THE TIMELINE LIVES IN THE HOOK, NEVER IN A DRAWING. A phone with no room
//   card mounted, or a desktop that never paints, still ends every walk: the
//   drawings only read `transit.phase`.
// ⭐⭐ `hold` keeps the PICTURE on the room you are leaving (GameStepPage
//   artRoom) until the new painting fills the screen; then the wallpaper swaps
//   UNDER the overlay and the overlay leaves MS.SETTLE later, so nothing flashes.
// ⭐ Any key or tap skips (armed a beat late, so the tap that chose the room
//   never skips its own walk).

// ─────────────────────────────── the timeline ───────────────────────────────
// phases: go → (wait) → open → expand → done   ·   go → refuse → gone
//         a slow house (desktop): go/wait → aside → fadein → done  (or aside → done)
let SEQ = 0;
const sleep = (ms) => new Promise((r) => setTimeout(r, Math.max(0, ms)));

export function useRoomTransit() {
  const [transit, setTransit] = useState(null);
  const api = useRef(null);
  if (!api.current) {
    const cur = { t: null };
    let timers = [];
    const later = (fn, ms) => { timers.push(setTimeout(fn, ms)); };
    const clearAll = () => { timers.forEach(clearTimeout); timers = []; };
    const live = (id) => !!cur.t && cur.t.id === id;
    const patch = (id, p) => {
      if (!live(id)) return false;
      cur.t = { ...cur.t, ...p };
      setTransit(cur.t);
      return true;
    };
    const drop = (id) => { if (live(id)) { cur.t = null; setTransit(null); } };
    const finish = (id) => {
      if (!live(id)) return;
      const c = cur.t;
      clearAll();
      const shownMs = Date.now() - c.t0;
      const refused = !!c.refused;
      // the wallpaper swaps NOW, under the overlay; the overlay leaves after it
      // (a door that opened onto no painting, or a refusal, has already faded)
      patch(id, { hold: false, phase: c.phase === 'gone' ? 'gone' : 'done' });
      later(() => drop(id), MS.SETTLE);
      if (typeof c.onSettle === 'function') {
        c.onSettle({ netMs: c.net == null ? shownMs : c.net, shownMs, refused });
      }
    };
    // 🐢 the walk stepped aside for a slow house: the page is back and the old
    // picture still shows, so the new painting FADES IN over it (the page steps
    // back again for the arrival card, exactly as it does today on arrival).
    // No painting (the main room) or a skip = land at once.
    const landAside = (id) => {
      if (!live(id)) return;
      const c = cur.t;
      if (c.art && !c.rush) {
        patch(id, { phase: 'fadein', curtain: true });
        later(() => finish(id), MS.FADE_IN);
      } else finish(id);
    };
    const reveal = (id) => {
      if (!live(id)) return;
      const c = cur.t;
      if (c.phase === 'aside') { landAside(id); return; }
      if (c.rush || c.kind === 'still') { finish(id); return; }
      if (c.kind === 'keyhole') {
        // the dark hall inside the keyhole lifts onto the NEW room: swap now
        patch(id, { phase: 'open', hold: false });
        later(() => finish(id), MS.IRIS_LIGHT + MS.IRIS_OPEN);
        return;
      }
      const openMs = c.kind === 'steps' ? MS.LAST_STEPS + MS.OPEN - 60 : MS.OPEN;
      patch(id, { phase: 'open' });
      later(() => patch(id, { phase: c.art ? 'expand' : 'gone' }), openMs);
      later(() => finish(id), openMs + (c.art ? MS.EXPAND : MS.GONE));
    };
    api.current = {
      /** Start a walk ON THE TAP. Returns its id. */
      begin(spec) {
        clearAll();
        const id = ++SEQ;
        cur.t = { ...spec, id, phase: 'go', hold: true, t0: Date.now(), net: null, rush: false, onSettle: null };
        setTransit(cur.t);
        if (spec.art) preloadArt(spec.art);
        if (spec.kind === 'door') later(() => { if (live(id) && cur.t.phase === 'go') patch(id, { phase: 'wait' }); }, MS.SLOW_AFTER);
        // 🐢 the page never waits on the house behind a walk longer than this:
        // a walk whose reveal has not begun steps aside (only walks that fade
        // the page have a curtain to lift; a phone walk lives in its card)
        if (spec.curtain) {
          later(() => {
            if (live(id) && (cur.t.phase === 'go' || cur.t.phase === 'wait')) patch(id, { phase: 'aside', curtain: false, aside: true });
          }, MS.CURTAIN_MAX);
        }
        later(() => finish(id), MS.SAFETY);
        return id;
      },
      /** The house answered. ok = it moved you; art = the painting to reveal
       *  (null = none: the door opens onto the dark and fades). onSettle runs
       *  once the room is on screen: { netMs, shownMs, refused }, or
       *  { superseded } (a newer walk took over), or null (this walk already
       *  ended, e.g. the safety timer: show the move the way it shows today). */
      answer(id, { ok, art, onSettle } = {}) {
        if (!live(id)) {
          if (typeof onSettle === 'function') onSettle(cur.t && cur.t.id > id ? { superseded: true } : null);
          return;
        }
        const c = cur.t;
        const net = Date.now() - c.t0;
        if (!ok && c.phase === 'aside') {
          // nothing on screen to rattle any more: straight to the house's words
          patch(id, { refused: true, net, onSettle });
          finish(id);
          return;
        }
        if (!ok) {
          patch(id, { phase: 'refuse', refused: true, net, onSettle });
          later(() => patch(id, { phase: 'gone' }), MS.REFUSE);
          later(() => finish(id), MS.REFUSE + MS.GONE);
          return;
        }
        const url = art || null;
        patch(id, { net, onSettle, art: url });
        const decoded = url ? Promise.race([preloadArt(url), sleep(MS.ART_WAIT_MAX)]) : Promise.resolve(true);
        // the keyhole must finish closing before it can open (else the close
        // lands after the open and darkens the new room)
        const floor = c.kind === 'keyhole' ? MS.IRIS_CLOSE : MS.ENTER;
        Promise.all([decoded, sleep(floor - net)]).then(() => reveal(id));
      },
      /** Any key or tap: skip the rest (instantly once the house has answered).
       *  A walk that stepped aside is not on screen: typing and tapping on the
       *  page it gave back skip nothing (and cost no re-render per key). */
      rush() {
        const c = cur.t;
        if (!c || c.phase === 'done' || c.phase === 'aside') return;
        if (c.net == null) { if (!c.rush) patch(c.id, { rush: true }); } else finish(c.id);
      },
      /** The request failed: no walk, no settle (the caller reports the error).
       *  A walk the house already answered plays out (a later failure, like
       *  the status refresh, must not swallow the move's own words). */
      cancel(id) { if (live(id) && cur.t.net == null) { clearAll(); drop(id); } },
    };
  }
  // any key or tap skips, armed a beat late (the choosing tap must not count)
  const tid = transit ? transit.id : null;
  useEffect(() => {
    if (tid == null) return undefined;
    const skip = () => api.current.rush();
    const arm = setTimeout(() => { window.addEventListener('keydown', skip); window.addEventListener('pointerdown', skip); }, 350);
    return () => { clearTimeout(arm); window.removeEventListener('keydown', skip); window.removeEventListener('pointerdown', skip); };
  }, [tid]);
  return { transit, begin: api.current.begin, answer: api.current.answer, rush: api.current.rush, cancel: api.current.cancel };
}

// ─────────────────────────────── the drawings ───────────────────────────────
const FOOT = (wet) => `<svg viewBox="0 0 32 22" aria-hidden="true"><g fill="${wet ? '#a9bdd2' : '#f5e7c6'}" stroke="#24170c" stroke-width="1.7"><ellipse cx="11.5" cy="11" rx="9.5" ry="7"/><circle cx="24.6" cy="17" r="2.9"/><circle cx="27.4" cy="11.8" r="2.4"/><circle cx="26.9" cy="6.9" r="2.1"/><circle cx="24.4" cy="3.3" r="1.8"/></g>${wet ? '<g fill="#a9bdd2"><circle cx="-2.5" cy="8" r="1.5"/><circle cx="-6.5" cy="13" r="1.1"/><circle cx="-4" cy="17.5" r=".8"/></g>' : ''}</svg>`;
const KEY = 'M -4.5 2.93 L -7.5 20 L 7.5 20 L 4.5 2.93 A 10 10 0 1 0 -4.5 2.93 Z';
const STEPS_N = 7;
const STARS = [[-5.5, -4, 0.9], [4.8, -5.6, 0.7], [6.2, 1.5, 1], [-6.4, 2.8, 0.6], [1.2, -7.2, 0.8], [-2.2, 5.6, 0.7], [3.4, 4.8, 0.6]];

const setStyles = (el, css) => { if (el) Object.assign(el.style, css); };
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

function useEntered() {
  const [entered, setEntered] = useState(false);
  useEffect(() => { const r = requestAnimationFrame(() => setEntered(true)); return () => cancelAnimationFrame(r); }, []);
  return entered;
}

/** The painting growing out of the doorway (a clip-path from the doorway's
 *  own box to the whole host), over the room you are leaving. */
function useExpand(active, hostRef, wayRef, nextRef) {
  useEffect(() => {
    if (!active) return;
    const host = hostRef.current, way = wayRef.current, next = nextRef.current;
    if (!host || !way || !next) return;
    const b = host.getBoundingClientRect(), r = way.getBoundingClientRect();
    const t = r.top - b.top, l = r.left - b.left, rr = b.right - r.right, bb = b.bottom - r.bottom, rad = r.width / 2;
    setStyles(next, { transition: 'none', clipPath: `inset(${t}px ${rr}px ${bb}px ${l}px round ${rad}px ${rad}px 3px 3px)`, opacity: '1' });
    let alive = true;
    nextFrame().then(() => {
      if (!alive) return;
      setStyles(next, { transition: `clip-path ${MS.EXPAND}ms cubic-bezier(.62,0,.22,1)`, clipPath: 'inset(0px 0px 0px 0px round 0px 0px 0px 0px)' });
    });
    return () => { alive = false; };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}

function Door({ transit, wayRef }) {
  const { phase, art } = transit;
  return (
    <>
      <div className="rt-door">
        <div className="rt-bob">
          <div ref={wayRef} className="rt-arch rt-way" style={art && phase !== 'go' && phase !== 'wait' ? { backgroundImage: `url("${art}")` } : undefined} />
          <div className="rt-arch rt-leaf"><span className="rt-win" /><span className="rt-knob" /></div>
          <span className="rt-knock rt-k1" /><span className="rt-knock rt-k2" />
        </div>
      </div>
      <div className="rt-mat" />
    </>
  );
}

function Steps({ transit, wayRef, onOpen }) {
  const stepsRef = useRef(null);
  const n = useRef(0);
  const { phase, wet, art } = transit;
  const put = (slot) => {
    const box = stepsRef.current;
    if (!box) return;
    const s = document.createElement('span');
    s.className = `rt-fp ${n.current % 2 === 0 ? 'rt-l' : 'rt-r'}`;
    s.style.left = `${(slot / (STEPS_N - 1)) * 88}%`;
    s.innerHTML = FOOT(!!wet);
    box.appendChild(s);
    n.current += 1;
    setTimeout(() => { if (s.parentNode) s.parentNode.removeChild(s); }, 1650);
  };
  const walking = phase === 'go' || phase === 'wait';
  useEffect(() => {
    if (!walking) return undefined;
    put(n.current % STEPS_N);
    const timer = setInterval(() => put(n.current % STEPS_N), MS.STEP);
    return () => clearInterval(timer);
  }, [walking]); // eslint-disable-line react-hooks/exhaustive-deps
  // the last two steps reach the door, then it opens
  useEffect(() => {
    if (phase !== 'open') return undefined;
    put(STEPS_N - 2);
    const a = setTimeout(() => put(STEPS_N - 1), 140);
    const b = setTimeout(onOpen, MS.LAST_STEPS);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rt-hall">
      <div className="rt-rug" />
      <div className="rt-steps" ref={stepsRef} />
      <div className="rt-sdoor">
        <div ref={wayRef} className="rt-arch rt-sway" style={art && phase !== 'go' && phase !== 'wait' ? { backgroundImage: `url("${art}")` } : undefined} />
        <div className="rt-arch rt-sleaf" />
      </div>
    </div>
  );
}

function Keyhole({ transit, variant }) {
  // ⭐ its OWN box, measured here: a child's layout effect runs before React
  // attaches the parent's ref, so the host's ref is still null at this point
  // (the 09-24 harness caught the keyhole never drawing).
  const boxRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const [dark, setDark] = useState(false);
  const pathsRef = useRef([]);
  const scale = useRef(0);
  const { phase, keyhole } = transit;
  useLayoutEffect(() => {
    const b = boxRef.current ? boxRef.current.getBoundingClientRect() : null;
    if (!b || !b.width || !b.height) return;
    const W = b.width, H = b.height;
    setGeo({ W, H, cx: W / 2, cy: H * 0.5, small: Math.min(W, H) * (variant === 'card' ? 0.0105 : 0.0088), big: Math.hypot(W, H) * 0.16, id: `rtkh${Math.random().toString(36).slice(2, 8)}` });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setS = (s) => {
    if (!geo) return;
    scale.current = s;
    pathsRef.current.forEach((p) => p && p.setAttribute('transform', `translate(${geo.cx} ${geo.cy}) scale(${s})`));
  };
  const tween = (a, z, ms, ease, alive) => new Promise((res) => {
    const t0 = performance.now();
    const step = (now) => {
      if (!alive()) { res(); return; }
      const k = Math.min(1, (now - t0) / ms);
      setS(a + (z - a) * ease(k));
      if (k < 1) requestAnimationFrame(step); else res();
    };
    requestAnimationFrame(step);
  });
  const easeIn = (k) => k * k * k;
  const easeOut = (k) => 1 - Math.pow(1 - k, 3);
  // close on mount
  useEffect(() => {
    if (!geo) return undefined;
    let alive = true;
    setS(geo.big);
    tween(geo.big, geo.small, MS.IRIS_CLOSE, easeIn, () => alive).then(() => { if (alive) setDark(true); });
    return () => { alive = false; };
  }, [geo]); // eslint-disable-line react-hooks/exhaustive-deps
  // open onto the new room, or back onto the old one after a refusal
  // (keyed on the direction, not the phase: open → done must not cut the tween)
  const reopen = (phase === 'open' || phase === 'done') ? 'open' : (phase === 'gone' ? 'gone' : null);
  useEffect(() => {
    if (!geo || !reopen) return undefined;
    let alive = true;
    setDark(false);
    const t = setTimeout(() => tween(scale.current || geo.small, geo.big, reopen === 'gone' ? MS.GONE : MS.IRIS_OPEN, easeOut, () => alive), reopen === 'open' ? MS.IRIS_LIGHT : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [reopen, geo]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!geo) return <div ref={boxRef} className="rt-kbox" />;
  const { W, H, cx, cy, small: s0, id } = geo;
  const cyC = cy - 6 * s0; // the keyhole's round top
  const at = (w, h, dy = 0) => ({ left: cx, top: cyC + dy * s0, width: w * s0, height: h * s0 });
  return (
    <div ref={boxRef} className="rt-kbox">
      <div className={`rt-khall${keyhole !== 'candle' ? ' rt-night' : ''}${dark ? ' rt-on' : ''}`}>
        {keyhole === 'beam' && (<><div className="rt-beam" style={at(60, 60)} /><div className="rt-lamp" style={at(3, 3)} /></>)}
        {keyhole === 'stars' && STARS.map(([x, y, r], i) => (
          <div key={i} className="rt-star" style={{ left: cx + x * s0, top: cyC + y * s0, width: r * 1.6 * s0, height: r * 1.6 * s0, animationDelay: `${i * 0.23}s` }} />
        ))}
        {keyhole === 'candle' && (<><div className="rt-glow" style={at(22, 22)} /><div className="rt-candle" style={{ left: cx, top: cyC + 5 * s0, width: 5 * s0, height: 17 * s0 }} /><div className="rt-flame" style={at(3.4, 5.6, 1.5)} /></>)}
      </div>
      <svg className="rt-ksvg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <defs>
          <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width={W} height={H}>
            <rect width={W} height={H} fill="#fff" />
            <path ref={(el) => { pathsRef.current[0] = el; }} d={KEY} fill="#000" transform={`translate(${cx} ${cy}) scale(${geo.big})`} />
          </mask>
        </defs>
        <rect width={W} height={H} fill="#0b0805" mask={`url(#${id})`} />
        <path ref={(el) => { pathsRef.current[1] = el; }} d={KEY} fill="none" stroke="#e9b565" strokeWidth="2.4" vectorEffect="non-scaling-stroke" transform={`translate(${cx} ${cy}) scale(${geo.big})`} />
      </svg>
    </div>
  );
}

/**
 * The drawing for one walk. `variant` = 'screen' (fixed over the whole window,
 * portaled to body, under the arrival card) or 'card' (inside the phone room
 * card at the top; unused since 09-25, kept for a one-line revert). Renders
 * only the walk whose `view` matches. `scrim` = the card's own gradient, laid
 * over the growing painting so the card looks the same the moment the overlay
 * leaves.
 * 📱 09-25 (Nina: "go"): phones walk on the WHOLE screen too. The phone card
 * scrolls away while you read the chat (measured: y = -269 after load), so the
 * card walk mostly played off screen. `z` lifts the overlay over the phone
 * dock (5000) and nav (5100); `fit` shows the WHOLE painting over a blurred
 * copy of itself, because the paintings are 1536x672 panoramas and covering a
 * 390x844 phone would show about a fifth of the room.
 */
export default function RoomTransit({ transit, variant = 'screen', scrim = null, z = 1990, fit = false }) {
  if (!transit || transit.view !== variant) return null;
  const node = <TransitDrawing key={transit.id} transit={transit} variant={variant} scrim={scrim} z={z} fit={fit} />;
  return variant === 'screen' && typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}

/** The whole painting over its own blur (phones). Shared with the page's phone
 *  postcard so the walk hands over to the card with no jump. */
export function FittedPainting({ art }) {
  return (
    <>
      <div className="rt-nblur" style={{ backgroundImage: `url("${art}")` }} />
      <div className="rt-nfit" style={{ backgroundImage: `url("${art}")` }} />
    </>
  );
}

function TransitDrawing({ transit, variant, scrim, z, fit }) {
  const hostRef = useRef(null);
  const wayRef = useRef(null);
  const nextRef = useRef(null);
  const entered = useEntered();
  const [stepsOpen, setStepsOpen] = useState(false);
  const { kind, phase, art, line, aside } = transit;
  // a walk that stepped aside never grows out of its doorway (its drawing is
  // long gone); it lands with the fade below instead
  const expanding = !aside && (phase === 'expand' || phase === 'done') && !!art && (kind === 'door' || kind === 'steps');
  useExpand(expanding, hostRef, wayRef, nextRef);
  // 🐢 the stepped-aside landing: the new painting fades in over the old one
  const fading = !!aside && !!art && (phase === 'fadein' || phase === 'done');
  useEffect(() => {
    const next = nextRef.current;
    if (!fading || !next) return undefined;
    setStyles(next, { transition: 'none', clipPath: 'none', opacity: '0' });
    let alive = true;
    nextFrame().then(() => { if (alive) setStyles(next, { transition: `opacity ${MS.FADE_IN}ms ease`, opacity: '1' }); });
    return () => { alive = false; };
  }, [fading]); // eslint-disable-line react-hooks/exhaustive-deps
  const opened = kind === 'door' ? ['open', 'expand', 'done'].includes(phase) : (kind === 'steps' && stepsOpen);
  const gone = !!aside || phase === 'gone' || (kind !== 'keyhole' && (phase === 'expand' || phase === 'done'));
  // the kind class is `rt-k-*`: a bare `rt-door` / `rt-steps` would pick up
  // the door's and the footprints' own styles (caught in the 09-24 harness)
  const cls = ['rt-grp', `rt-k-${kind}`,
    entered ? 'rt-in' : '',
    phase === 'wait' ? 'rt-slow' : '',
    opened ? 'rt-open' : '',
    phase === 'refuse' ? 'rt-rattle' : '',
    gone ? 'rt-gone' : '',
    kind === 'keyhole' && phase === 'open' ? 'rt-capoff' : '',
  ].filter(Boolean).join(' ');
  const hostStyle = variant === 'screen'
    ? { position: 'fixed', inset: 0, zIndex: z }
    : { position: 'absolute', inset: 0, zIndex: 3, borderRadius: 'inherit' };
  // 📱 a phone's dock + nav sit outside the page fade, so the fitted walk keeps
  // a solid backdrop of its own until the painting has filled the screen (the
  // veil fades with the drawings while the doorway grows). Never while stepped
  // aside (the page is yours again), never for the keyhole (it opens onto the page).
  const backOn = fit && kind !== 'keyhole' && !aside && phase !== 'gone';
  return (
    <div ref={hostRef} className={`rt-host rt-${variant}${fit ? ' rt-fitted' : ''}`} data-testid="room-transit" data-kind={kind} data-phase={phase} style={hostStyle} aria-hidden="true">
      {fit && <div className={`rt-back${entered && backOn ? ' rt-on' : ''}`} />}
      <div className={cls}>
        {kind !== 'keyhole' && <div className="rt-veil" />}
        {kind === 'door' && <Door transit={transit} wayRef={wayRef} />}
        {kind === 'steps' && <Steps transit={transit} wayRef={wayRef} onOpen={() => setStepsOpen(true)} />}
        {kind === 'keyhole' && <Keyhole transit={transit} variant={variant} />}
        {line && <div className="rt-cap">{transit.refused ? REFUSE_LINE : line}</div>}
      </div>
      {(kind === 'door' || kind === 'steps' || aside) && art && (fit ? (
        <div ref={nextRef} className="rt-next rt-fit"><FittedPainting art={art} /></div>
      ) : (
        <div ref={nextRef} className="rt-next" style={{ backgroundImage: `${scrim ? `${scrim}, ` : ''}url("${art}")` }} />
      ))}
    </div>
  );
}

// ─────────────────────────────── the styles ───────────────────────────────
// Sized off the host with container units (cqmin/cqw): the same drawing fits
// the whole window on desktop and the room card on a phone.
if (typeof document !== 'undefined' && !document.head.querySelector('style[data-room-transit]')) {
  const sheet = document.createElement('style');
  sheet.setAttribute('data-room-transit', 'true');
  sheet.textContent = `
.rt-host { container-type: size; pointer-events: none; overflow: hidden; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
.rt-grp { position: absolute; inset: 0; transition: opacity ${MS.GONE}ms ease; }
.rt-grp.rt-gone { opacity: 0; }
.rt-next { position: absolute; inset: 0; opacity: 0; background-position: center; background-size: cover; background-repeat: no-repeat; }
.rt-fit { overflow: hidden; background: #0b0704; }
.rt-nblur { position: absolute; inset: -8%; background: center / cover no-repeat; filter: blur(14px) brightness(.5); }
.rt-nfit { position: absolute; inset: 0; background: center 42% / contain no-repeat; }
.rt-veil { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 55%, rgba(12,8,5,.3), rgba(12,8,5,.8)); opacity: 0; transition: opacity .28s ease; }
.rt-card .rt-veil { background: rgba(12,8,5,.62); }
/* 📱 phones: the dock + nav are fixed chrome the page fade may not reach, so the veil is nearly solid */
.rt-fitted .rt-veil { background: radial-gradient(ellipse at 50% 55%, rgba(12,8,5,.88), rgba(12,8,5,.96)); }
.rt-back { position: absolute; inset: 0; background: #0c0805; opacity: 0; transition: opacity .28s ease; }
.rt-back.rt-on { opacity: 1; }
.rt-in .rt-veil { opacity: 1; }
.rt-cap { position: absolute; left: 0; right: 0; top: 21%; padding-inline: 6%; text-align: center; font-weight: 800; color: #fff; font-size: clamp(12px, 4.5cqmin, 24px); text-shadow: 0 2px 12px rgba(0,0,0,.95), 0 1px 3px rgba(0,0,0,.9); opacity: 0; transform: translateY(6px); transition: opacity .3s ease .08s, transform .3s ease .08s; }
.rt-card .rt-cap { top: 8%; font-size: clamp(11px, 8.2cqmin, 15px); }
.rt-in .rt-cap { opacity: 1; transform: none; }
.rt-capoff .rt-cap { opacity: 0; transition-delay: 0s; }
.rt-k-still .rt-cap { top: 46%; transition: none; }
.rt-k-still .rt-veil { transition: none; }
.rt-arch { position: absolute; inset: 0; box-sizing: border-box; border-radius: 50% 50% 3% 3% / 30% 30% 2% 2%; border: max(2px, .85cqmin) solid #24170c; }

/* B · the little door */
.rt-door { position: absolute; left: 50%; top: 58%; width: 23cqmin; height: 34cqmin; transform: translate(-50%, -50%) scale(.55); opacity: 0; transition: transform .36s cubic-bezier(.3,1.6,.5,1), opacity .18s ease; }
.rt-card .rt-door { top: 60%; width: 31cqmin; height: 46cqmin; }
.rt-in .rt-door { transform: translate(-50%, -50%) scale(1); opacity: 1; }
.rt-bob { position: absolute; inset: 0; transform-origin: 50% 100%; }
.rt-way { background: #0b0704 center / cover no-repeat; box-shadow: inset 0 0 0 max(2px, .7cqmin) #3a2616; }
.rt-leaf { background: repeating-linear-gradient(90deg, #b4532f 0 23%, #9c4527 23% 25%); transform-origin: 0 50%; transform: perspective(420px) rotateY(0deg); transition: transform ${MS.OPEN}ms cubic-bezier(.55,0,.3,1), filter ${MS.OPEN}ms ease; }
.rt-win { position: absolute; left: 50%; top: 15%; width: 38%; aspect-ratio: 1; transform: translateX(-50%); border-radius: 50%; box-sizing: border-box; background: radial-gradient(circle at 42% 38%, #fff6d0, #ffd98a 45%, #e3a04a); border: max(2px, .75cqmin) solid #24170c; }
.rt-win::before, .rt-win::after { content: ""; position: absolute; background: #24170c; }
.rt-win::before { left: 50%; top: 0; bottom: 0; width: max(1.5px, .45cqmin); transform: translateX(-50%); }
.rt-win::after { top: 50%; left: 0; right: 0; height: max(1.5px, .45cqmin); transform: translateY(-50%); }
.rt-knob { position: absolute; right: 10%; top: 55%; width: 12%; aspect-ratio: 1; border-radius: 50%; box-sizing: border-box; background: radial-gradient(circle at 35% 35%, #ffe9b0, #e9b565 55%, #a8742e); border: max(1.5px, .55cqmin) solid #24170c; }
.rt-mat { position: absolute; left: 50%; top: calc(58% + 18cqmin); width: 31cqmin; height: 4.2cqmin; transform: translateX(-50%); box-sizing: border-box; background: repeating-linear-gradient(90deg, #6f8a4a 0 11%, #5d783c 11% 22%); border: max(2px, .75cqmin) solid #24170c; border-radius: 1.2cqmin; opacity: 0; transition: opacity .2s ease .1s; }
.rt-card .rt-mat { top: calc(60% + 24.5cqmin); width: 40cqmin; height: 5cqmin; }
.rt-in .rt-mat { opacity: 1; }
.rt-knock { position: absolute; top: 30%; width: 14%; height: 20%; box-sizing: border-box; border-radius: 50%; border: max(2px, .75cqmin) solid transparent; opacity: 0; }
.rt-k1 { left: -25%; border-left-color: #ffd98a; }
.rt-k2 { right: -25%; border-right-color: #ffd98a; }
.rt-slow .rt-bob { animation: rtKnock 1.15s ease-in-out infinite; }
.rt-slow .rt-knob { animation: rtJiggle 1.15s ease-in-out infinite; }
.rt-slow .rt-k1 { animation: rtKnockL 1.15s ease-out infinite; }
.rt-slow .rt-k2 { animation: rtKnockR 1.15s ease-out infinite; }
.rt-open .rt-bob, .rt-open .rt-knob, .rt-open .rt-knock { animation: none; }
.rt-open .rt-leaf { transform: perspective(420px) rotateY(-106deg); filter: brightness(.62); }
.rt-rattle .rt-bob, .rt-rattle .rt-sdoor, .rt-rattle .rt-ksvg { animation: rtRattle .11s linear 7; }
.rt-rattle .rt-knob { animation: rtJig .14s linear 6; }

/* A · footsteps */
.rt-hall { position: absolute; left: 50%; top: 60%; width: min(64cqw, 104cqmin); height: 12cqmin; transform: translate(-50%, -50%); opacity: 0; transition: opacity .28s ease; }
.rt-card .rt-hall { top: 62%; width: 86cqw; height: 17cqmin; }
.rt-in .rt-hall { opacity: 1; }
.rt-rug { position: absolute; left: 1.8cqmin; right: 13cqmin; top: 0; bottom: 0; box-sizing: border-box; border-radius: 1.2cqmin; background: linear-gradient(180deg, #8d3526, #782b1e); border: max(2px, .8cqmin) solid #24170c; box-shadow: inset 0 0 0 max(2px, .8cqmin) #7a2c1f, inset 0 0 0 max(3px, 1.3cqmin) #d8b98a; }
.rt-rug::before, .rt-rug::after { content: ""; position: absolute; top: 12%; bottom: 12%; width: 1.8cqmin; background: repeating-linear-gradient(180deg, #d8b98a 0 .9cqmin, transparent .9cqmin 1.8cqmin); }
.rt-rug::before { left: -2.6cqmin; }
.rt-rug::after { right: -2.6cqmin; }
.rt-steps { position: absolute; left: 3cqmin; right: 15cqmin; top: 0; bottom: 0; }
.rt-fp { position: absolute; top: 50%; width: 5.6cqmin; height: 3.9cqmin; animation: rtFp 1.6s ease-out forwards; }
.rt-card .rt-fp { width: 7.4cqmin; height: 5.1cqmin; }
.rt-fp svg { display: block; width: 100%; height: 100%; overflow: visible; }
.rt-fp.rt-l { margin-top: -4.4cqmin; }
.rt-fp.rt-r { margin-top: .5cqmin; }
.rt-card .rt-fp.rt-l { margin-top: -5.8cqmin; }
.rt-card .rt-fp.rt-r { margin-top: .7cqmin; }
.rt-fp.rt-r svg { transform: scaleY(-1); }
.rt-sdoor { position: absolute; right: 0; bottom: 30%; width: 12cqmin; height: 18cqmin; }
.rt-card .rt-sdoor { width: 15cqmin; height: 23cqmin; }
.rt-sdoor .rt-arch { border-width: max(1.5px, .6cqmin); }
.rt-sway { background: #0b0704 center / cover no-repeat; }
.rt-sleaf { background: repeating-linear-gradient(90deg, #b4532f 0 23%, #9c4527 23% 25%); transform-origin: 0 50%; transform: perspective(260px) rotateY(0); transition: transform .42s cubic-bezier(.55,0,.3,1), filter .42s; }
.rt-sleaf::after { content: ""; position: absolute; right: 12%; top: 52%; width: 14%; aspect-ratio: 1; border-radius: 50%; background: #e9b565; border: max(1px, .4cqmin) solid #24170c; }
.rt-open .rt-sleaf { transform: perspective(260px) rotateY(-106deg); filter: brightness(.62); }

/* C · the keyhole */
.rt-kbox { position: absolute; inset: 0; }
.rt-khall { position: absolute; inset: 0; background: #0b0805; opacity: 0; transition: opacity ${MS.IRIS_LIGHT}ms ease; }
.rt-khall.rt-night { background: radial-gradient(ellipse at 50% 40%, #18213d, #070a16 70%); }
.rt-khall.rt-on { opacity: 1; }
.rt-khall > div { position: absolute; }
.rt-glow { border-radius: 50%; background: radial-gradient(circle, rgba(255,196,110,.6), rgba(255,160,70,.14) 45%, transparent 70%); transform: translate(-50%, -50%); animation: rtGlow 1.7s ease-in-out infinite; }
.rt-flame { transform: translate(-50%, -50%); background: radial-gradient(ellipse at 50% 70%, #fff6d8, #ffd98a 45%, #f08a2a 82%); border-radius: 50% 50% 50% 50% / 62% 62% 38% 38%; animation: rtFlick .45s ease-in-out infinite alternate; }
.rt-candle { transform: translateX(-50%); box-sizing: border-box; background: linear-gradient(90deg, #efe0bd, #f7ecd2 40%, #d9c49a); border: max(1.5px, .5cqmin) solid #24170c; border-radius: .6cqmin .6cqmin .3cqmin .3cqmin; }
.rt-beam { border-radius: 50%; transform: translate(-50%, -50%); background: conic-gradient(from 0deg, rgba(255,232,170,0) 0deg, rgba(255,232,170,.9) 16deg, rgba(255,232,170,0) 38deg, rgba(255,232,170,0) 180deg, rgba(255,232,170,.55) 196deg, rgba(255,232,170,0) 218deg, rgba(255,232,170,0) 360deg); animation: rtBeam 1.8s linear infinite; }
.rt-lamp { border-radius: 50%; background: #fff3c4; box-shadow: 0 0 14px 7px rgba(255,220,140,.85); transform: translate(-50%, -50%); }
.rt-star { border-radius: 50%; background: #fff6d8; box-shadow: 0 0 6px 2px rgba(255,240,200,.7); transform: translate(-50%, -50%); animation: rtTwinkle 1.6s ease-in-out infinite; }
.rt-ksvg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }

@keyframes rtKnock { 0%, 55%, 100% { transform: none; } 62% { transform: rotate(-1.8deg); } 70% { transform: rotate(1.3deg); } 78% { transform: rotate(-.6deg); } 86% { transform: none; } }
@keyframes rtJiggle { 0%, 52%, 100% { transform: rotate(0); } 58% { transform: rotate(-42deg); } 66% { transform: rotate(26deg); } 74% { transform: rotate(0); } }
@keyframes rtKnockL { 0%, 57% { opacity: 0; transform: translateX(35%) scale(.7); } 64% { opacity: 1; } 92%, 100% { opacity: 0; transform: translateX(-12%) scale(1.06); } }
@keyframes rtKnockR { 0%, 57% { opacity: 0; transform: translateX(-35%) scale(.7); } 64% { opacity: 1; } 92%, 100% { opacity: 0; transform: translateX(12%) scale(1.06); } }
@keyframes rtRattle { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-2.5%); } 75% { transform: translateX(2.5%); } }
@keyframes rtJig { 0%, 100% { transform: rotate(0); } 50% { transform: rotate(-30deg); } }
@keyframes rtFp { 0% { opacity: 0; transform: scale(.4); } 12% { opacity: 1; transform: scale(1.12); } 22% { transform: scale(1); } 65% { opacity: 1; } 100% { opacity: 0; } }
@keyframes rtFlick { from { transform: translate(-50%, -50%) scale(1, 1) rotate(-4deg); } to { transform: translate(-50%, -54%) scale(.9, 1.1) rotate(4deg); } }
@keyframes rtGlow { 0%, 100% { opacity: .85; } 50% { opacity: 1; } }
@keyframes rtBeam { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }
@keyframes rtTwinkle { 0%, 100% { opacity: .45; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .rt-bob, .rt-knob, .rt-knock, .rt-fp, .rt-flame, .rt-glow, .rt-beam, .rt-star { animation: none !important; }
}
`;
  document.head.appendChild(sheet);
}
